/* Grubwick admin — icon set + venue metadata. The Logo lives in components/assets/Logo. */
import React from "react";
import type { VenueType } from "../../../data/adminData";

export { Logo } from "../../../assets/Logo";

interface IconProps {
  size?: number;
  sw?: number;
}

function base({ size = 18, sw = 2 }: IconProps): React.SVGProps<SVGSVGElement> {
  return {
    viewBox: "0 0 24 24",
    width: size,
    height: size,
    fill: "none",
    stroke: "currentColor",
    strokeWidth: sw,
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": true,
  } as React.SVGProps<SVGSVGElement>;
}

export const Store = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M3 9l1.5-5h15L21 9M4 9v10h16V9M4 9h16M9 19v-5h6v5" />
  </svg>
);
export const Plus = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <line x1="12" y1="5" x2="12" y2="19" />
    <line x1="5" y1="12" x2="19" y2="12" />
  </svg>
);
export const Camera = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z" />
    <circle cx="12" cy="13" r="4" />
  </svg>
);
export const Chart = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <line x1="4" y1="20" x2="4" y2="10" />
    <line x1="10" y1="20" x2="10" y2="4" />
    <line x1="16" y1="20" x2="16" y2="13" />
    <line x1="22" y1="20" x2="22" y2="20" />
    <line x1="2" y1="20" x2="22" y2="20" />
  </svg>
);
export const Tags = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M20.6 13.4 13.4 20.6a2 2 0 0 1-2.8 0L3 13V3h10l7.6 7.6a2 2 0 0 1 0 2.8z" />
    <circle cx="8" cy="8" r="1.6" />
  </svg>
);
export const Search = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <circle cx="11" cy="11" r="7" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
);
export const Pin = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M12 21s-6.5-6-6.5-11a6.5 6.5 0 0 1 13 0c0 5-6.5 11-6.5 11Z" />
    <circle cx="12" cy="10" r="2.2" />
  </svg>
);
export const Arrow = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <line x1="4" y1="12" x2="19" y2="12" />
    <polyline points="13 6 19 12 13 18" />
  </svg>
);
export const Back = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <line x1="20" y1="12" x2="5" y2="12" />
    <polyline points="11 18 5 12 11 6" />
  </svg>
);
export const Pencil = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4z" />
  </svg>
);
export const Trash = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);
export const X = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);
export const Check = (p: IconProps = {}) => (
  <svg {...base({ ...p, sw: p.sw || 2.4 })}>
    <polyline points="4 12 10 18 20 6" />
  </svg>
);
export const Menu = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <line x1="3" y1="6" x2="21" y2="6" />
    <line x1="3" y1="12" x2="21" y2="12" />
    <line x1="3" y1="18" x2="21" y2="18" />
  </svg>
);
export const Image = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <rect x="3" y="3" width="18" height="18" rx="2.5" />
    <circle cx="8.5" cy="8.5" r="1.6" />
    <path d="m21 15-5-5L5 21" />
  </svg>
);
/* Document with text lines — used as the thumbnail placeholder for an uploaded
 * PDF, which can't render in an <img>. */
export const FileText = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z" />
    <polyline points="14 3 14 9 20 9" />
    <line x1="8" y1="13" x2="16" y2="13" />
    <line x1="8" y1="17" x2="13" y2="17" />
  </svg>
);
export const Upload = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);
export const Sparkle = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z" />
    <path d="M19 15l.7 1.8L21.5 18l-1.8.7L19 20l-.7-1.3L16.5 18l1.8-.7z" />
  </svg>
);
export const Link = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M10 13a5 5 0 0 0 7 0l3-3a5 5 0 0 0-7-7l-1.5 1.5" />
    <path d="M14 11a5 5 0 0 0-7 0l-3 3a5 5 0 0 0 7 7l1.5-1.5" />
  </svg>
);
/* Scan / read — corner viewfinder with a sweep line. Used for menu extraction
 * (reads as "scan the menu"), deliberately not the AI-cliché sparkle. */
export const Scan = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M4 8V6a2 2 0 0 1 2-2h2" />
    <path d="M16 4h2a2 2 0 0 1 2 2v2" />
    <path d="M20 16v2a2 2 0 0 1-2 2h-2" />
    <path d="M8 20H6a2 2 0 0 1-2-2v-2" />
    <path d="M4 12h16" />
  </svg>
);
export const Chevron = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <polyline points="6 9 12 15 18 9" />
  </svg>
);
/* Loading spinner — pair with the `spin` class to animate. */
export const Spinner = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);
export const Info = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <circle cx="12" cy="12" r="9" />
    <line x1="12" y1="11" x2="12" y2="16" />
    <circle cx="12" cy="8" r="0.6" fill="currentColor" />
  </svg>
);
/* Kebab — vertical "more actions" affordance. */
export const Kebab = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <circle cx="12" cy="5" r="1" fill="currentColor" />
    <circle cx="12" cy="12" r="1" fill="currentColor" />
    <circle cx="12" cy="19" r="1" fill="currentColor" />
  </svg>
);
/* Grip — six-dot drag handle for reorderable rows. */
export const Grip = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <circle cx="9" cy="6" r="1" fill="currentColor" />
    <circle cx="9" cy="12" r="1" fill="currentColor" />
    <circle cx="9" cy="18" r="1" fill="currentColor" />
    <circle cx="15" cy="6" r="1" fill="currentColor" />
    <circle cx="15" cy="12" r="1" fill="currentColor" />
    <circle cx="15" cy="18" r="1" fill="currentColor" />
  </svg>
);
export const List = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <circle cx="3.5" cy="6" r="1" />
    <circle cx="3.5" cy="12" r="1" />
    <circle cx="3.5" cy="18" r="1" />
  </svg>
);

/* DropMascot — the menu-photo character for the import dropzone. A little
 * polaroid of a menu page with the Grubwick face, raised arms and feet, plus an
 * upload badge. Shares the logo's ink-outline + warm-palette language (see
 * assets/Logo). Fixed colors by design, so it isn't a `currentColor` icon. */
export const DropMascot = ({ size = 96 }: { size?: number } = {}) => (
  <svg width={size} height={size} viewBox="0 0 96 96" fill="none" aria-hidden="true">
    <ellipse cx="48" cy="88" rx="23" ry="3" fill="#2A1E18" opacity="0.11" />
    <g transform="rotate(-6 48 50)">
      {/* arms raised in a cute little cheer — drawn behind the card so they
         appear to come from its sides; round "hands" at the tips */}
      <path d="M26 45 Q17 41 16 31" stroke="#2A1E18" strokeWidth="2.4" strokeLinecap="round" fill="none" />
      <circle cx="15.5" cy="29.5" r="3" fill="#fff" stroke="#2A1E18" strokeWidth="2" />
      <path d="M70 45 Q79 41 80 31" stroke="#2A1E18" strokeWidth="2.4" strokeLinecap="round" fill="none" />
      <circle cx="80.5" cy="29.5" r="3" fill="#fff" stroke="#2A1E18" strokeWidth="2" />
      {/* legs + feet */}
      <path d="M40 76 L40 84" stroke="#2A1E18" strokeWidth="2" strokeLinecap="round" />
      <path d="M56 76 L56 84" stroke="#2A1E18" strokeWidth="2" strokeLinecap="round" />
      <ellipse cx="38" cy="85" rx="3.4" ry="2" fill="#fff" stroke="#2A1E18" strokeWidth="1.8" />
      <ellipse cx="58" cy="85" rx="3.4" ry="2" fill="#fff" stroke="#2A1E18" strokeWidth="1.8" />
      {/* polaroid card */}
      <rect x="24" y="18" width="48" height="60" rx="7" fill="#fff" stroke="#2A1E18" strokeWidth="2.4" />
      {/* the "photo" — a little menu page scene */}
      <clipPath id="dm-photo"><rect x="29" y="23" width="38" height="30" rx="3" /></clipPath>
      <g clipPath="url(#dm-photo)">
        <rect x="29" y="23" width="38" height="30" fill="#FDF3E6" />
        {/* menu header bar */}
        <rect x="41" y="26.5" width="14" height="2.4" rx="1.2" fill="#F47C2C" />
        {/* item rows: name (teal) + price (maroon), like the logo's menu lines */}
        <rect x="33" y="33" width="20" height="1.8" rx="0.9" fill="#0E7C6F" />
        <rect x="58.5" y="33" width="5" height="1.8" rx="0.9" fill="#A12A4A" />
        <rect x="33" y="38" width="16" height="1.8" rx="0.9" fill="#0E7C6F" />
        <rect x="58.5" y="38" width="5" height="1.8" rx="0.9" fill="#A12A4A" />
        <rect x="33" y="43" width="22" height="1.8" rx="0.9" fill="#0E7C6F" />
        <rect x="58.5" y="43" width="5" height="1.8" rx="0.9" fill="#A12A4A" />
        <rect x="33" y="48" width="15" height="1.8" rx="0.9" fill="#0E7C6F" />
        <rect x="58.5" y="48" width="5" height="1.8" rx="0.9" fill="#A12A4A" />
      </g>
      {/* face on the caption strip */}
      <circle cx="37" cy="66" r="2" fill="#F58A6A" opacity="0.5" />
      <circle cx="59" cy="66" r="2" fill="#F58A6A" opacity="0.5" />
      <g className="dm-eye">
        <circle cx="41" cy="63" r="2" fill="#2A1E18" />
        <circle cx="41.6" cy="62.4" r="0.55" fill="#fff" />
      </g>
      <g className="dm-eye">
        <circle cx="55" cy="63" r="2" fill="#2A1E18" />
        <circle cx="55.6" cy="62.4" r="0.55" fill="#fff" />
      </g>
      <path d="M42 68 Q48 73 54 68" stroke="#2A1E18" strokeWidth="2" strokeLinecap="round" />
    </g>
    {/* upload badge */}
    <circle cx="75" cy="70" r="12" fill="#0E7C6F" stroke="#fff" strokeWidth="3" />
    <path d="M75 75.5 L75 64.5 M70.5 69 L75 64.5 L79.5 69" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* venue type → color + icon */
const Utensils = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M5 3v7a2 2 0 0 0 4 0V3M7 10v11M17 3c-1.5 0-3 1.5-3 4s1.5 4 3 4v10" />
  </svg>
);
const Tent = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M3 20h18M12 4 3 20M12 4l9 16M12 9l-5 11M12 9l5 11" />
  </svg>
);
const HomeIc = (p: IconProps = {}) => (
  <svg {...base(p)}>
    <path d="M4 11 12 4l8 7M6 10v9h12v-9" />
  </svg>
);

export interface VenueMeta {
  color: string;
  deep: string;
  Icon: (p: IconProps) => React.ReactElement;
}

export const VENUE_META: Record<VenueType, VenueMeta> = {
  Restaurant: {
    color: "var(--teal)",
    deep: "var(--teal-deep)",
    Icon: Utensils,
  },
  "Farmers Market": {
    color: "var(--maroon)",
    deep: "var(--maroon)",
    Icon: Tent,
  },
  "Micro Kitchen": {
    color: "var(--amber)",
    deep: "var(--amber)",
    Icon: HomeIc,
  },
};
